import {
  keyStorage,
  TOKEN_DATA,
  MarketTypes,
  MarketTypesIds,
  SIDEBAR_OPTIONS,
  MarketsValueTypeIds,
  SYMBOL_REAL_TIME_TYPES,
  UserPermission
} from 'constants/globals'
import INDEX_FTSE_UNIQUE_KEYS from 'constants/uniqueKeys'
import { User, TokenData } from 'constants/types'
import Token from './token'
import jwtDecode from 'jwt-decode'
import UserService from '../services/auth'
import { ITESM_CLIENT_QUERY, PATH_LOGIN } from 'routes/paths'
import { infoselSDK } from 'services/infosel-sdk'

/** Evita jwt-decode y JSON.parse repetidos en el hot path (p. ej. cada render con useLightstreamerClient). */
const MAX_PARSE_JWT_CACHE = 8
const parseJwtByRaw = new Map<string, TokenData>()
/** Disparado tras `setCurrentUser` / `logoutUser` para sincronizar clientes fuera de Redux (p. ej. CopilotKit). */
export const AUTH_TOKEN_UPDATED_EVENT = 'auth-token-updated' as const

export function parseJwt(token: Token | string) {
  // `token` puede llegar como:
  // - string JWT (cuando se usa user.tokenData en algunos flows)
  // - instancia de `Token` (cuando se usa después del login)
  // Para evitar valores corruptos de `exp/iat`, debemos decodificar el JWT real.
  const rawJwt = typeof token === 'string' ? token : token?.accessToken || token?.token
  if (!rawJwt) {
    return {} as unknown as ReturnType<typeof jwtDecode>
  }
  const cached = parseJwtByRaw.get(rawJwt)
  if (cached) {
    return cached
  }
  const decoded = jwtDecode(rawJwt) as TokenData
  if (parseJwtByRaw.size >= MAX_PARSE_JWT_CACHE) {
    const firstKey = parseJwtByRaw.keys().next().value as string | undefined
    if (firstKey !== undefined) parseJwtByRaw.delete(firstKey)
  }
  parseJwtByRaw.set(rawJwt, decoded)
  return decoded
}

let cachedUserSessionRaw: string | null = null
let cachedUserResult: User | null = null

/** Llamar tras mutar session user fuera de setCurrentUser si hiciera falta coherencia inmediata. */
export function invalidateCurrentUserCache(): void {
  cachedUserSessionRaw = null
  cachedUserResult = null
  parseJwtByRaw.clear()
}

export function getCurrentUser(): User | null {
  const user = sessionStorage.getItem(keyStorage.user)
  if (!user) {
    cachedUserSessionRaw = null
    cachedUserResult = null
    return null
  }
  if (user === cachedUserSessionRaw && cachedUserResult !== null) {
    return { ...cachedUserResult }
  }
  const userToken = JSON.parse(user)
  const token = userToken.token
  const tokenParsed: TokenData = parseJwt(token)

  const built: User = {
    ...userToken,
    username: tokenParsed.preferred_username,
    firstName: tokenParsed.given_name,
    lastName: tokenParsed.family_name,
    email: tokenParsed.email
  }
  cachedUserSessionRaw = user
  cachedUserResult = built
  return { ...built }
}

export function getIfTickerVisible(): boolean {
  const user = sessionStorage.getItem(keyStorage.user)
  return user ? JSON.parse(user).tickerVisible : false
}

export function getLSUrl(): string {
  return String(import.meta.env.VITE_LS_URL)
}

export function getLSUrlMD(): string {
  return String(import.meta.env.VITE_MD2_URL)
}

export function getApiUrl(): string {
  return String(import.meta.env.VITE_WORKSPACE_URL)
}

const cleanTokenData = (): void => {
  TOKEN_DATA.hasPermissionToPosturesBMV = false
  TOKEN_DATA.hasPermissionToInternationalExchanges = false
  TOKEN_DATA.hasPermissionToSectors = false
  TOKEN_DATA.hasPermissionToInvestmentFunds = false
  TOKEN_DATA.isBmvDelay = false
  TOKEN_DATA.isBivaDelay = false
  TOKEN_DATA.isInternationalBagsDelay = false
  TOKEN_DATA.isAmexDelay = false
  TOKEN_DATA.isNyseDelay = false
  TOKEN_DATA.isNasdaqDelay = false
  TOKEN_DATA.isIndexBmvDelay = false
  TOKEN_DATA.isIndexJdiAndSPDelay = false
  TOKEN_DATA.data = null

  SIDEBAR_OPTIONS.forEach((option) => {
    if (option && option.value === 'Posturas') {
      option.disabled = false
      option.iconType = ''
      option.titleTooltip = ''
    }
  })
}

const ROLE_INTERNATIONAL_BAGS_TR = 'BI_RT'
const ROLE_POSTURAS = 'DEPTH_20_BMV'
const ROLE_POSTURAS_BIVA = 'DEPTH_20_BIVA'
const ROLE_POSTURAS_CONSOLIDATED = 'DEPTH_20_CONS'
const ROLE_SECTORIAL = 'SECTOR'
const ROLE_USER = 'user'
const ROLE_FONDOS = 'FONDOS'
const ROLE_BMV_DELAY = 'BMV_DL'
const ROLE_BIVA_DELAY = 'BIVA_DL'
const ROLE_INTERNATIONAL_BAGS_DELAY = 'BI_DL'
const ROLE_AMEX_DELAY = 'AMEX_DL'
const ROLE_NYSE_DELAY = 'NYSE_DL'
const ROLE_NASDAQ_DELAY = 'NASDAQ_DL'
const ROLE_INDEX_BMV_DELAY = 'INDICES_BMV_DL'
const ROLE_INDEX_DJI_SP_DELAY = 'DJI_SP_DL'
const ROLE_MT_NEWSWIRES = 'MTN'
const ROLE_GUBERNAMENTAL_DEBT = 'GUBERNAMENTAL_DEBT_RT'
const ROLE_INTERNATIONAL_DEBT = 'INTERNATIONAL_DEBT_RT'
const ROLE_PRIVATE_DEBT = 'PRIVATE_DEBT_RT'
const ROLE_BROKERAGE_FIRMS = 'BROKERAGE_FIRMS_REPORTS'
const ROLE_CONSOLIDATED_RT = 'CONS_RT'
const ROLE_CONSOLIDATED_DL = 'CONS_DL'

const setPermissionMarket = (roles: Array<string>): void => {
  TOKEN_DATA.permissions = [...TOKEN_DATA.permissions, ...roles]
  const hasPermissionToPosturesBMV = roles.includes(ROLE_POSTURAS)
  const hasPermissionToPosturesBIVA = roles.includes(ROLE_POSTURAS_BIVA)
  const hasPermissionToPosturesCons = roles.includes(ROLE_POSTURAS_CONSOLIDATED)
  const hasPermissionToInternationalExchanges = roles.includes(ROLE_INTERNATIONAL_BAGS_TR)
  const hasPermissionToSectors = roles.includes(ROLE_SECTORIAL)
  const hasPermissionToInvestmentFunds = roles.includes(ROLE_FONDOS)
  const isBmvDelay = roles.includes(ROLE_BMV_DELAY)
  const isBivaDelay = roles.includes(ROLE_BIVA_DELAY)
  const isInternationalBagsDelay = roles.includes(ROLE_INTERNATIONAL_BAGS_DELAY)
  const isAmexDelay = roles.includes(ROLE_AMEX_DELAY)
  const isNyseDelay = roles.includes(ROLE_NYSE_DELAY)
  const isNasdaqDelay = roles.includes(ROLE_NASDAQ_DELAY)
  const isIndexBmvDelay = roles.includes(ROLE_INDEX_BMV_DELAY)
  const isIndexJdiAndSPDelay = roles.includes(ROLE_INDEX_DJI_SP_DELAY)
  const hasPermissionToGovernmentDebt = roles.includes(ROLE_GUBERNAMENTAL_DEBT)
  const hasPermissionToInternationalDebt = roles.includes(ROLE_INTERNATIONAL_DEBT)
  const hasPermissionToBrokerageFirms = roles.includes(ROLE_BROKERAGE_FIRMS)
  const hasPermissionToPrivateDebt = roles.includes(ROLE_PRIVATE_DEBT)
  const hasPermissionToConsolidated = roles.includes(ROLE_CONSOLIDATED_RT) || roles.includes(ROLE_CONSOLIDATED_DL)

  TOKEN_DATA.hasPermissionToPosturesBMV = hasPermissionToPosturesBMV
  TOKEN_DATA.hasPermissionToPosturesBIVA = hasPermissionToPosturesBIVA
  TOKEN_DATA.hasPermissionToPosturesCons = hasPermissionToPosturesCons
  TOKEN_DATA.hasPermissionToInternationalExchanges = hasPermissionToInternationalExchanges
  TOKEN_DATA.hasPermissionToSectors = hasPermissionToSectors
  TOKEN_DATA.hasPermissionToInvestmentFunds = hasPermissionToInvestmentFunds
  TOKEN_DATA.isBmvDelay = isBmvDelay
  TOKEN_DATA.isBivaDelay = isBivaDelay
  TOKEN_DATA.isInternationalBagsDelay = isInternationalBagsDelay
  TOKEN_DATA.isAmexDelay = isAmexDelay
  TOKEN_DATA.isNyseDelay = isNyseDelay
  TOKEN_DATA.isNasdaqDelay = isNasdaqDelay
  TOKEN_DATA.isIndexBmvDelay = isIndexBmvDelay
  TOKEN_DATA.isIndexJdiAndSPDelay = isIndexJdiAndSPDelay
  TOKEN_DATA.hasPermissionToGovernmentDebt = hasPermissionToGovernmentDebt
  TOKEN_DATA.hasPermissionToInternationalDebt = hasPermissionToInternationalDebt
  TOKEN_DATA.hasPermissionToPrivateDebt = hasPermissionToPrivateDebt
  TOKEN_DATA.hasPermissionToBrokerageFirms = hasPermissionToBrokerageFirms
  TOKEN_DATA.hasPermissionToConsolidated = hasPermissionToConsolidated
}

const setPermissionFactset = (): void => {
  let hasPermissionToRecommendation = false
  const apiFactset = 'api-factset'
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiFactset] &&
    TOKEN_DATA.data.resource_access[apiFactset].roles
  ) {
    TOKEN_DATA.permissions = [...TOKEN_DATA.permissions, ...TOKEN_DATA.data.resource_access[apiFactset].roles]
    hasPermissionToRecommendation = TOKEN_DATA.data.resource_access[apiFactset].roles.includes(ROLE_USER)
  }
  TOKEN_DATA.hasPermissionToRecommendation = hasPermissionToRecommendation
}

const setPermissionNews = (): void => {
  let hasPermissionToNewsMtNewswires = false
  const apiNews = 'api-news'
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiNews] &&
    TOKEN_DATA.data.resource_access[apiNews].roles
  ) {
    TOKEN_DATA.permissions = [...TOKEN_DATA.permissions, ...TOKEN_DATA.data.resource_access[apiNews].roles]
    TOKEN_DATA.permissions = TOKEN_DATA.permissions.filter((rol) => {
      const nameRol = rol.replace(/_TR|_RT|_DL/g, '')
      if (rol.includes('DL')) return !TOKEN_DATA.permissions.includes(`${nameRol}_RT`)
      return true
    })
    hasPermissionToNewsMtNewswires = TOKEN_DATA.data.resource_access[apiNews].roles.includes(ROLE_MT_NEWSWIRES)
  }
  TOKEN_DATA.hasPermissionToNewsMtNewswires = hasPermissionToNewsMtNewswires
}

const setPermissionByToken = (): void => {
  const apiMarketData = 'api-market-data'
  const apiHubRol = 'hub-roles'
  const apiAiAssistant = 'infosel-ia'
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiMarketData] &&
    TOKEN_DATA.data.resource_access[apiMarketData].roles
  ) {
    let roleMarkets = TOKEN_DATA.data.resource_access[apiMarketData].roles || []
    roleMarkets = roleMarkets.filter((rol) => {
      const nameRol = rol.replace(/_TR|_RT|_DL/g, '')
      if (rol.includes('_DL')) {
        return !roleMarkets.includes(`${nameRol}_RT`)
      }
      return true
    })
    setPermissionMarket(roleMarkets)
  }
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiHubRol] &&
    TOKEN_DATA.data.resource_access[apiHubRol].roles &&
    TOKEN_DATA.data.resource_access[apiHubRol].roles.length > 0
  ) {
    const hubRol = TOKEN_DATA.data.resource_access[apiHubRol].roles.find((rol) => rol !== 'hub-md2') || ''
    const hubMd2 = !!TOKEN_DATA.data.resource_access[apiHubRol].roles.find((rol) => rol === 'hub-md2')
    TOKEN_DATA.hubRol = hubRol || ''
    TOKEN_DATA.hasPermissionToMd2 = hubMd2
  }
  const apiMexder = 'api-mexder'
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiMexder] &&
    TOKEN_DATA.data.resource_access[apiMexder].roles
  ) {
    const ROLE_MEXDER = 'DERIVADOS_MEXDER'
    const rolesMexder = TOKEN_DATA.data.resource_access[apiMexder].roles
    const hasPermissionToMexder = rolesMexder.includes(ROLE_MEXDER)
    TOKEN_DATA.hasPermissionToMexder = hasPermissionToMexder
  }
  if (
    TOKEN_DATA.data &&
    TOKEN_DATA.data.resource_access &&
    TOKEN_DATA.data.resource_access[apiAiAssistant] &&
    TOKEN_DATA.data.resource_access[apiAiAssistant].roles
  ) {
    const rolesAiAssistant = 'Asistente inteligente'
    const hasPermissionToAiAssistant = TOKEN_DATA.data.resource_access[apiAiAssistant].roles.includes(rolesAiAssistant)
    TOKEN_DATA.hasPermissionToChaAI = hasPermissionToAiAssistant
  }

  setPermissionFactset()
  setPermissionNews()
}

export const setTokenData = (token: Token | string): void => {
  if (token && !TOKEN_DATA.data) {
    const tokenParsed: TokenData = parseJwt(token)
    TOKEN_DATA.data = tokenParsed
    setPermissionByToken()
  }
}

export function setCurrentUser(user: User, token: Token): void {
  const accessToken = user.token
  const tokenParsed: TokenData = parseJwt(token)
  setTokenData(token)
  const serverDate = new Date(tokenParsed.iat * 1000)
  const currentDate = new Date()
  const diferenceTime = serverDate.getTime() - currentDate.getTime()
  const obj = {
    token: user.token,
    accessToken,
    exp: tokenParsed.exp,
    iat: tokenParsed.iat,
    expRefresh: user.expRefresh,
    tickerVisible: user?.tickerVisible || false,
    refreshToken: user.refreshToken,
    diferenceTime,
    userId: tokenParsed.sub,
    tokenData: accessToken
  }
  const result = sessionStorage.setItem(keyStorage.user, JSON.stringify(obj))
  invalidateCurrentUserCache()
  infoselSDK.resetInstance()

  // Notifica cambios de auth para componentes que dependen de token fuera de Redux.
  // (Ej: CopilotKit headers runtimeUrl necesitan refrescarse cuando el token cambia.)
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_TOKEN_UPDATED_EVENT))
    }
  } catch {
    // ignore
  }

  return result
}

export function setCurrentUserTickerVisible(tickerVisible: boolean): void {
  const user = sessionStorage.getItem(keyStorage.user)
  let obj = { tickerVisible: false }
  if (user) {
    obj = JSON.parse(user)
    obj.tickerVisible = tickerVisible
  }

  return sessionStorage.setItem(keyStorage.user, JSON.stringify(obj))
}

function safeLocalStorageGetItem(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined' || typeof localStorage.getItem !== 'function') {
      return null
    }
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeLocalStorageRemoveItem(key: string): void {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
      localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
}

/** Limpia restos de sesión de Keycloak en storage local (el iframe KC no pone httpOnly en el mismo origen de la app). */
function clearKeycloakClientStorage(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      const lower = k.toLowerCase()
      if (k === 'isUserItesm' || k.startsWith('kc-') || lower.includes('keycloak') || lower.includes('oidc')) {
        toRemove.push(k)
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k))
  } catch {
    // ignore
  }
}

export function logoutUser(): void {
  cleanTokenData()
  infoselSDK.resetInstance()
  const isUserItesm = safeLocalStorageGetItem('isUserItesm') === 'true'
  sessionStorage.clear()
  invalidateCurrentUserCache()

  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_TOKEN_UPDATED_EVENT))
    }
  } catch {
    // ignore
  }

  if (isUserItesm) {
    try {
      UserService.clearToken()
    } catch {
      // ignore
    }
    safeLocalStorageRemoveItem('isUserItesm')
    clearKeycloakClientStorage()
    // Login del Hub (misma app), no pantalla de logout de Keycloak
    window.location.replace(`${window.location.origin}${PATH_LOGIN}?${ITESM_CLIENT_QUERY}`)
    return
  }
}

const validatePermissionsInternationalBagsByUser = (valueTypeId: number): string => {
  let newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.REAL_TIME
  switch (valueTypeId) {
    case MarketsValueTypeIds.AMEX:
      if (TOKEN_DATA.isAmexDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    case MarketsValueTypeIds.NYSE:
      if (TOKEN_DATA.isNyseDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    case MarketsValueTypeIds.NASDAQ:
      if (TOKEN_DATA.isNasdaqDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    default:
      break
  }
  return newOpportunityInformation
}

/** El usuario contrató el permiso `FTSE` (`TOKEN_DATA.permissions`). */
export const hasFtsePermission = (): boolean => TOKEN_DATA.permissions?.includes(UserPermission.ROLE_FTSE) ?? false

/**
 * El usuario contrató Profundidad de Posturas en al menos un mercado (BMV, BIVA o Consolidado).
 * Única fuente para habilitar la tarjeta en "Agregar tarjeta"; el menú de fila de las tablas
 * valida por mercado de cada instrumento y no usa este helper.
 */
export const hasPosturesDepthPermission = (): boolean =>
  Boolean(
    TOKEN_DATA.hasPermissionToPosturesBMV ||
      TOKEN_DATA.hasPermissionToPosturesBIVA ||
      TOKEN_DATA.hasPermissionToPosturesCons
  )

/**
 * Oportunidad de información de los índices FTSE (`INDEX_FTSE_UNIQUE_KEYS`).
 *
 * El permiso `FTSE` del admin no tiene par `_RT` / `_DL`: quien lo trae ve los
 * FTSE en tiempo real y quien no, diferidos. Es la única fuente de esta regla;
 * la usan las tablas, el snapshot y la tarjeta de Índices globales.
 */
export const getFtseOpportunityInformation = (): string =>
  hasFtsePermission() ? SYMBOL_REAL_TIME_TYPES.REAL_TIME : SYMBOL_REAL_TIME_TYPES.DELAY

const validatePermissionsIndexByUser = (
  opportunityInformation: string,
  marketTypeId: number,
  uniqueKey: string
): string => {
  let newOpportunityInformation = opportunityInformation || SYMBOL_REAL_TIME_TYPES.REAL_TIME
  switch (marketTypeId) {
    case MarketTypesIds.INDEX:
      if (TOKEN_DATA.isIndexBmvDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    case MarketTypesIds.INDEX_DJI_SP:
      if (INDEX_FTSE_UNIQUE_KEYS.includes(uniqueKey)) {
        newOpportunityInformation = getFtseOpportunityInformation()
      }
      break
    default:
      break
  }
  return newOpportunityInformation
}

const getValuesByUniqueKey = (uniqueKey: string): { marketTypeId: number; valueTypeId: number } => {
  if (!uniqueKey) return { marketTypeId: 0, valueTypeId: 0 }
  const uniqueKeys = uniqueKey.split('/')
  if (uniqueKey) {
    return {
      marketTypeId: Number(uniqueKeys[0]),
      valueTypeId: Number(uniqueKeys[1])
    }
  }
  return { marketTypeId: 0, valueTypeId: 0 }
}

export const getOpportunityInformationByMarketId = (
  opportunityInformation: string,
  uniqueKey: string,
  marketId: number,
  doubleCheck?: boolean
): string => {
  const { marketTypeId } = getValuesByUniqueKey(uniqueKey)
  // El valor del backend para un FTSE no refleja el permiso del usuario: siempre pasa por la regla.
  const isFtseIndex =
    marketId === MarketTypes.INDEX.id &&
    marketTypeId === MarketTypesIds.INDEX_DJI_SP &&
    INDEX_FTSE_UNIQUE_KEYS.includes(uniqueKey)
  if (opportunityInformation && !doubleCheck && !isFtseIndex) return opportunityInformation
  let newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.REAL_TIME
  switch (marketId) {
    case MarketTypes.BMV.id:
      if (TOKEN_DATA.isBmvDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    case MarketTypes.BIVA.id:
      if (TOKEN_DATA.isBivaDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      }
      break
    case MarketTypes.INTERNATIONAL_BAGS.id:
      if (TOKEN_DATA.isInternationalBagsDelay) {
        newOpportunityInformation = SYMBOL_REAL_TIME_TYPES.DELAY
      } else if (!TOKEN_DATA.hasPermissionToInternationalExchanges) {
        const { valueTypeId } = getValuesByUniqueKey(uniqueKey)
        newOpportunityInformation = validatePermissionsInternationalBagsByUser(valueTypeId)
      }
      break
    case MarketTypes.INDEX.id: {
      newOpportunityInformation = validatePermissionsIndexByUser(opportunityInformation, marketTypeId, uniqueKey)
      break
    }
    default:
      break
  }
  return newOpportunityInformation
}
